import {
  clubFeederBarTone,
  clubFeederDaysLabel,
  clubFeederFillPercent,
  clubFeederLbsPerWeek,
  clubFeederMeta,
  clubFeederNeedsFill,
  isClubFeedLocation,
} from './clubWaypointBoard';
import { isStandFeature } from './pointIdentityPayload';

const STAND_ICONS = new Set(['tree-stand', 'box-blind']);
const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

function byName(a, b) {
  return String(a?.name || '').localeCompare(String(b?.name || ''), undefined, {
    sensitivity: 'base',
  });
}

export function geoTypeOf(feature) {
  return feature?.geojson?.type || feature?.geojson?.geometry?.type || '';
}

/** Parcel unions often save as MultiPolygon; that is still an area. */
export function isAreaGeometryType(type) {
  return type === 'Polygon' || type === 'MultiPolygon';
}

export function isLandFeature(feature) {
  const type = geoTypeOf(feature);
  return isAreaGeometryType(type) || type === 'LineString';
}

/**
 * AREA chip is keyed as Polygon. MultiPolygon must match that chip or it
 * would vanish again the moment the user filters to areas.
 */
export function featureBoardTypeKey(feature) {
  const type = geoTypeOf(feature);
  return isAreaGeometryType(type) ? 'Polygon' : type;
}

export function isPersonalFeedLocation(feature) {
  if (isClubFeedLocation(feature)) return true;
  if (geoTypeOf(feature) !== 'Point') return false;
  const marker = String(feature?.marker_type || '').toLowerCase();
  const icon = String(feature?.icon || '').toLowerCase();
  return marker === 'feeder' || icon === 'feeder';
}

export function isPersonalStandLocation(feature) {
  if (geoTypeOf(feature) !== 'Point') return false;
  const icon = String(feature?.icon || '').toLowerCase();
  if (STAND_ICONS.has(icon)) return true;
  if (feature?.is_stand != null) return Boolean(feature.is_stand);
  return isStandFeature(feature);
}

export function isFeatureHidden(feature, hiddenFeatureIds) {
  if (!hiddenFeatureIds || hiddenFeatureIds.size === 0 || feature?.id == null) return false;
  return (
    hiddenFeatureIds.has(feature.id) ||
    hiddenFeatureIds.has(String(feature.id)) ||
    hiddenFeatureIds.has(Number(feature.id))
  );
}

export function partitionFeatureBoardRows(features = []) {
  const land = [];
  const feeders = [];
  const stands = [];
  const markers = [];
  (Array.isArray(features) ? features : []).forEach((feature) => {
    if (isLandFeature(feature)) {
      land.push(feature);
      return;
    }
    if (geoTypeOf(feature) !== 'Point') return;
    if (isPersonalFeedLocation(feature)) {
      feeders.push(feature);
      return;
    }
    if (isPersonalStandLocation(feature)) {
      stands.push(feature);
      return;
    }
    markers.push(feature);
  });
  land.sort(byName);
  feeders.sort(byName);
  stands.sort(byName);
  markers.sort(byName);
  return { land, points: [...feeders, ...stands, ...markers], feeders, stands, markers };
}

function plural(count, one, many) {
  return `${count} ${count === 1 ? one : many}`;
}

export function featureBoardCounts(features = [], { hiddenFeatureIds } = {}) {
  void hiddenFeatureIds;
  const list = Array.isArray(features) ? features : [];
  const featureCount = list.length;
  const feederCount = list.filter(isPersonalFeedLocation).length;
  const needFillCount = list.filter(
    (feature) => isPersonalFeedLocation(feature) && clubFeederNeedsFill(feature)
  ).length;
  const parts = [plural(featureCount, 'FEATURE', 'FEATURES')];
  if (feederCount > 0) parts.push(plural(feederCount, 'FEEDER', 'FEEDERS'));
  const headline = parts.join(' · ');
  const need = needFillCount > 0 ? `${needFillCount} NEED FILL` : '';
  return {
    featureCount,
    feederCount,
    needFillCount,
    headline,
    line: need ? `${headline} · ${need}` : headline,
  };
}

export function landAcreageTotal(land = []) {
  return (Array.isArray(land) ? land : []).reduce((sum, feature) => {
    const acres = Number(feature?.acreage);
    return Number.isFinite(acres) ? sum + acres : sum;
  }, 0);
}

export function formatAcChip(acres) {
  const n = Number(acres);
  if (!Number.isFinite(n)) return '— AC';
  return `${String(Math.round(n * 100) / 100)} AC`;
}

export function formatMiChip(miles) {
  const n = Number(miles);
  if (!Number.isFinite(n)) return '— MI';
  return `${String(Math.round(n * 100) / 100)} MI`;
}

function formatEta(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return `ETA ${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`;
}

export function featureBoardChip(feature, { hidden = false, now = new Date() } = {}) {
  if (hidden) return { text: 'HIDDEN', kind: 'hidden' };
  if (isPersonalFeedLocation(feature) && feature?.feeding_is_active === false) {
    return { text: 'OFF', kind: 'off' };
  }
  const type = geoTypeOf(feature);
  if (isAreaGeometryType(type)) return { text: formatAcChip(feature?.acreage), kind: 'area' };
  if (type === 'LineString') return { text: formatMiChip(feature?.length), kind: 'line' };
  if (isPersonalFeedLocation(feature)) {
    const tone = clubFeederBarTone(feature);
    if (tone === 'low') {
      const pct = clubFeederFillPercent(feature);
      if (pct <= 0) return { text: 'EMPTY', kind: 'feeder-low' };
      return { text: `${Math.round(pct)}%`, kind: 'feeder-low' };
    }
    const pct = Math.round(clubFeederFillPercent(feature));
    const days = clubFeederDaysLabel(feature, now);
    return { text: `${pct}% · ${days}`, kind: tone === 'watch' ? 'feeder-watch' : 'feeder-ok' };
  }
  if (isPersonalStandLocation(feature)) return { text: 'STAND', kind: 'stand' };
  return { text: 'MARKER', kind: 'marker' };
}

function standKindLabel(feature) {
  const icon = String(feature?.icon || '').toLowerCase();
  if (icon === 'box-blind') return 'Box blind';
  if (icon === 'tree-stand') return 'Tree stand';
  return 'Stand';
}

function bestWindLabel(feature) {
  const winds = Array.isArray(feature?.optimal_winds)
    ? feature.optimal_winds.filter(Boolean)
    : [];
  if (winds.length) return `best wind ${winds.join('/')}`;
  if (feature?.optimal_wind) return `best wind ${feature.optimal_wind}`;
  return '';
}

function markerKindLabel(feature) {
  const raw = String(feature?.marker_type || '').trim();
  if (!raw) return 'Marker';
  return raw.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
}

export function featureBoardMeta(feature) {
  if (isPersonalFeedLocation(feature)) {
    const tail = clubFeederMeta(feature);
    return tail ? `Feeder · ${tail}` : 'Feeder';
  }
  if (isLandFeature(feature)) {
    return geoTypeOf(feature) === 'LineString' ? 'Line' : 'Area';
  }
  if (isPersonalStandLocation(feature)) {
    return [standKindLabel(feature), bestWindLabel(feature)].filter(Boolean).join(' · ');
  }
  const kind = markerKindLabel(feature);
  return kind === 'Marker' ? 'Marker' : `Marker · ${kind}`;
}

export function featureBoardFeederStatusLine(feature, now = new Date()) {
  const week = clubFeederLbsPerWeek(feature);
  const rate = week != null ? `${week} LB/WEEK` : null;
  const eta = formatEta(feature?.refill_due_date);
  return [rate, eta].filter(Boolean).join(' · ');
}

export function featureBoardName(feature) {
  const name = String(feature?.name || '').trim();
  return name || 'Unnamed';
}
